import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import IntegrationCard from '../ui/IntegrationCard';
import DisconnectModal from '../ui/DisconnectModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

const Integrations = () => {
  const INSTAGRAM_LIMIT = 4;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [searchParams] = useSearchParams();
  const [callbackProcessing, setCallbackProcessing] = useState(false);

  // Instagram OAuth URL
  // const authUrl = "https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=704964296044481&redirect_uri=https://dev.uttik.com/integrations/instagram&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights";
  const authUrl = "https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=704964296044481&redirect_uri=https://app.uttik.com/integrations/instagram&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights";
  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getInstagramStatus();
      if (data && data.success && data.accounts) {
        setInstagramAccounts(data.accounts);
      } else {
        setInstagramAccounts([]);
      }
    } catch (err) {
      console.error("Error fetching Instagram status:", err);
      setError("Failed to fetch Instagram integration status.");
      toast.error("Could not load Instagram integration status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const code = searchParams.get('code');
    const oauthErrorParam = searchParams.get('error_description') || searchParams.get('error');

    if (oauthErrorParam) {
      const errorMsg = `Instagram authentication failed: ${oauthErrorParam}`;
      toast.error(errorMsg);
      navigate('/integrations/instagram', { replace: true });
      fetchStatus();
      return;
    }

    if (code) {
      const handleCallback = async () => {
        try {
          setCallbackProcessing(true);
          const response = await ApiService.submitInstagramAuthCallback(code);
          if (response?.success) {
            toast.success('Successfully integrated with Instagram!');
            if (response.accounts?.length > 0) {
              const newAcc = response.accounts[response.accounts.length - 1];
              navigate(`/integrations/instagram/${newAcc.integration_id}`, { replace: true });
            } else {
              navigate('/integrations', { replace: true });
            }
          } else {
            throw new Error(response?.message || 'Callback API was not successful.');
          }
        } catch (err) {
          console.error('Error handling Instagram OAuth callback:', err);
          const rawMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Authentication failed. Please try connecting again.';
          toast.error(rawMsg);
          navigate('/integrations/instagram', { replace: true });
          fetchStatus();
        } finally {
          setCallbackProcessing(false);
        }
      };
      handleCallback();
    } else {
      fetchStatus();
    }
  }, []);

  const handleConnect = () => {
    window.location.href = authUrl;
  };

  const handleManage = (id) => {
    navigate(`/integrations/instagram/${id}`);
  };

  const handleAutomation = (id) => {
    navigate(`/integrations/instagram/${id}?tab=automation`);
  };

  const handleDisconnect = async () => {
    const integrationId = selectedIntegrationId;
    if (!integrationId) return;

    try {
      setIsDisconnecting(true);
      const response = await ApiService.disconnectInstagram(integrationId);
      if (response && response.success) {
        toast.success("Instagram integration disconnected successfully.");
        // update the account status in local state instead of removing it
        setInstagramAccounts((prev) => prev.map(a => 
          a.integration_id === integrationId 
            ? { ...a, overall_status: 'disconnected' } 
            : a
        ));
        setIsDisconnectModalOpen(false);
        setSelectedIntegrationId(null);
      } else {
        throw new Error(response?.message || "Failed to disconnect.");
      }
    } catch (err) {
      console.error("Error disconnecting Instagram:", err);
      toast.error(err.message || "Failed to disconnect Instagram.");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const instagramIntegrationBase = {
    name: 'Instagram',
    description: 'Automatically respond to comments and DMs on your Instagram posts.',
    logo: 'https://cdn.simpleicons.org/instagram/E4405F',
    category: 'Social',
    capabilities: ['social', 'automation'],
  };

  return (
    <div className="integrations-root p-6 sm:p-8">
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/integrations')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all hover:scale-110"
            title="Back to Integrations"
          >
            <ArrowLeft className="h-6 w-6 text-gray-700" />
          </button>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">Instagram Integrations</h1>
            <p className="text-gray-500 font-medium">Connect your Instagram tools & automate responses with AI</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto mt-8">
        {callbackProcessing ? (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center min-h-[300px]">
            <Loader2 className="h-10 w-10 text-black animate-spin mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Connecting Instagram</h2>
            <p className="text-gray-500 font-medium">Exchanging authorization code and subscribing to webhooks. This will take a moment…</p>
          </div>
        ) : loading ? (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center min-h-[300px]">
            <Loader2 className="h-10 w-10 text-black animate-spin mb-4" />
            <p className="text-gray-500 font-medium animate-pulse">Loading Instagram status...</p>
          </div>
        ) : error ? (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center text-center min-h-[300px]">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-4 border border-red-100">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{error}</h3>
            <p className="text-gray-500 max-w-sm mb-6">
              There was an issue checking your Instagram connection status. Please check your network or try again.
            </p>
            <button
              onClick={fetchStatus}
              className="flex items-center gap-2 px-5 py-2.5 bg-black text-white font-bold rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {instagramAccounts.length === 0 && (
                <IntegrationCard
                  integration={{ ...instagramIntegrationBase, status: 'not_connected', account: null }}
                  onConnect={instagramAccounts.length < INSTAGRAM_LIMIT ? handleConnect : () => toast.error('Account limit reached')}
                  onManage={handleManage}
                  onAutomation={handleAutomation}
                  onDisconnect={(id) => { setSelectedIntegrationId(id); setIsDisconnectModalOpen(true); }}
                />
              )}

              {instagramAccounts.map((acc) => (
                <IntegrationCard
                  key={acc.integration_id}
                  integration={{ ...instagramIntegrationBase, status: acc.overall_status === 'active' ? 'connected' : 'connected', account: acc }}
                  onConnect={handleConnect}
                  onManage={() => handleManage(acc.integration_id)}
                  onAutomation={() => handleAutomation(acc.integration_id)}
                  onDisconnect={(id) => { setSelectedIntegrationId(id || acc.integration_id); setIsDisconnectModalOpen(true); }}
                />
              ))}

              {instagramAccounts.length > 0 && instagramAccounts.length < INSTAGRAM_LIMIT && (
                <button
                  onClick={handleConnect}
                  className="group relative flex flex-col items-center justify-center p-6 bg-white border-2 border-dashed border-gray-200 rounded-[1.5rem] hover:border-black hover:bg-gray-50/50 transition-all duration-300 min-h-[220px]"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <RefreshCw className="w-6 h-6 text-gray-400 group-hover:text-black" />
                  </div>
                  <span className="text-gray-900 font-bold text-sm">Connect another account</span>
                  <p className="text-gray-500 text-[11px] mt-1 text-center">Add another Instagram business profile</p>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <DisconnectModal 
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnect}
        integrationName="Instagram"
        isLoading={isDisconnecting}
      />
    </div>
  );
};

export default Integrations;
